import React from 'react';
import {
  DayInfo,
  OpeningTimes as GqlOpeningTimes
} from '../../../../../types/graphql-types-generated.ts';
import { nonNullElement } from '../../../../../utils/non-null-element.ts';
import { groupBy, isNonEmptyArray } from '../../../../../utils/array.tsx';
import { sortBy } from '../../../../../utils/sort.ts';

type LabeledDayInfo = {
  label: string;
  hours: Array<DayInfo | null> | null;
};

export const openingTimesEmpty = (openingTimes: GqlOpeningTimes | null): boolean =>
  !openingTimes ||
  [
    openingTimes.tuesday,
    openingTimes.wednesday,
    openingTimes.thursday,
    openingTimes.friday,
    openingTimes.saturday,
    openingTimes.sunday
  ].every(hours => hours === null || hours.filter(nonNullElement).length === 0);

export type OpeningTimesProps = {
  openingTimes: GqlOpeningTimes;
};

export const OpeningTimes: React.FunctionComponent<OpeningTimesProps> = props => {
  const { openingTimes } = props;

  const dayToString = (hours: DayInfo[]) =>
    hours !== null && hours.map(h => h && `${h.from}${h.to}`).join();
  const mondayString =
    openingTimes.monday && dayToString(openingTimes.monday.filter(nonNullElement));

  const allDays: LabeledDayInfo[] = [
    { label: 'Montag', hours: openingTimes.monday },
    { label: 'Dienstag', hours: openingTimes.tuesday },
    { label: 'Mittwoch', hours: openingTimes.wednesday },
    { label: 'Donnerstag', hours: openingTimes.thursday },
    { label: 'Freitag', hours: openingTimes.friday },
    { label: 'Samstag', hours: openingTimes.saturday },
    { label: 'Sonntag', hours: openingTimes.sunday }
  ];

  const allDaysEqual: boolean = allDays.every(
    day => day.hours && dayToString(day.hours.filter(nonNullElement)) === mondayString
  );

  const renderOpeningHours = (day: LabeledDayInfo) => (
    <div className="mb-1" key={day.label}>
      <div>
        <strong>{day.label}</strong>
      </div>
      {isNonEmptyArray(day.hours?.filter(nonNullElement)) ? (
        day.hours.filter(nonNullElement).map(({ from, to }) => (
          <div key={`${from}${to}`} className="text-sm">
            {from} - {to} Uhr
          </div>
        ))
      ) : (
        <>Geschlossen</>
      )}
    </div>
  );

  if (allDaysEqual && openingTimes.monday) {
    return renderOpeningHours({ label: 'Montag bis Sonntag', hours: openingTimes.monday });
  } else {
    const daysGroupedByOpeningTimes = sortBy(
      Array.from(
        groupBy(allDays, day => day.hours && dayToString(day.hours.filter(nonNullElement)))
      ).map(([_, days]) => days),
      days => [allDays.findIndex(day => day.label === days[0].label)]
    );

    return (
      <>
        {daysGroupedByOpeningTimes.map(days =>
          renderOpeningHours({ label: days.map(d => d.label).join(', '), hours: days[0].hours })
        )}
      </>
    );
  }
};
